import type { FC } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import type { BoxProps } from '@mui/material';
import { Box } from '@mui/material';
import { useAtomValue, useSetAtom } from 'jotai';
import { useAtomCallback } from 'jotai/utils';

import { SECTION } from 'src/constants/section';
import type { PlanActionPermissionOrgUnit } from 'src/types/orgUnit';
import type { ActionPlan } from 'src/types/planAction';
import { apiPaths } from 'src/services/api/apiPaths';
import HttpService from 'src/services/api/http';
import history from 'src/services/routing/history';
import { routePaths } from 'src/services/routing/routePaths';
import useUserPermissions from 'src/components/app/context/UserContext/useUserPermissions';
import type { PageFrameProps } from 'src/components/common/PageFrame';
import PageFrame from 'src/components/common/PageFrame';
import OrgUnitHeader from 'src/components/planAction/OrgUnitHeader';
import { usePlanActionContext } from 'src/components/planAction/PlanActionContext';

import Action from './components/Action';
import { actionsAtom, apiActionSelector } from './components/Action/atoms';
import { useTouchActionPlan } from './components/Action/hooks';
import CreateActionButton from './components/CreateActionButton';
import PlanActionMeta from './components/Meta';

import { styles } from './index.styles';

export interface PlanActionDetailPageProps extends BoxProps, PageFrameProps {}

const PlanActionDetailPage: FC<PlanActionDetailPageProps> = ({ sx = [] }) => {
  const { getPermissionForModuleType } = useUserPermissions();
  const { id } = useParams<{ id?: string }>();
  const { touchActionPlan, resetTouched } = useTouchActionPlan();
  const [editable, setEditable] = useState(false);
  const actionPlanId = id && parseInt(id);
  const actions = useAtomValue(actionsAtom);
  const setActions = useSetAtom(apiActionSelector);
  const [actionPlanValue, setActionPlanValue] = useState<ActionPlan>({
    actions: [],
    creator: '',
    id: undefined,
    name: '',
    org_unit: 0,
    status: 0,
    updated: '',
  });

  const {
    actionPlans,
    deleteActionPlan,
    errors,
    resetErrors,
    saveActionPlan,
    selectedOrgUnit,
  } = usePlanActionContext();

  const actionPlan = useMemo(
    () => actionPlans.find(({ id }) => id === actionPlanId),
    [actionPlanId, actionPlans],
  );

  // ? This looks super awkward.
  // ? Error responses should not be treated as valid answer to determine user permissions.
  async function checkForWritePermission() {
    const orgUnitWithWritePermissionIds = getPermissionForModuleType(
      SECTION.PLAN_ACTION,
    )
      ?.org_units.filter(
        (org_unit) => !(org_unit as PlanActionPermissionOrgUnit).read_only,
      )
      ?.map((org_unit) => org_unit.id);

    setEditable(
      !!selectedOrgUnit &&
        (await HttpService.get(
          `${apiPaths.ORG_UNIT}${selectedOrgUnit.id}/is_descendant_of/`,
          { ancestor_candidates: orgUnitWithWritePermissionIds },
        )
          .then(() => true)
          .catch(() => false)),
    );
  }

  /**
   * Handle name change.
   */
  const handleNameChange = (value: string) => {
    setActionPlanValue((prevState) => {
      if (value !== prevState.name) touchActionPlan();

      return { ...prevState, name: value };
    });
  };

  /**
   * Handle delete.
   */
  const handleDelete = () =>
    !!actionPlan?.id && deleteActionPlan(actionPlan.id);

  /**
   * Handle save.
   */
  const handleSave = useAtomCallback(
    useCallback(
      (get) => {
        resetTouched();
        const apiActions = get(apiActionSelector);
        if (apiActions) {
          saveActionPlan({ ...actionPlanValue, actions: apiActions });
        } else {
          touchActionPlan();
        }
      },
      [actionPlanValue, saveActionPlan, touchActionPlan, resetTouched],
    ),
  );

  useEffect(() => {
    setActions([]);
    resetTouched();
  }, [setActions, resetTouched]);

  useEffect(() => {
    if (!selectedOrgUnit) {
      history.replace(routePaths.PLAN_ACTION_OVERVIEW);
    }

    if (actionPlan) {
      if (actionPlan.id !== actionPlanValue.id) {
        setActionPlanValue(actionPlan);
        setActions(actionPlan.actions);
        resetTouched();
      } else if (actionPlan.status !== actionPlanValue.status) {
        setActionPlanValue(actionPlan);
      }
    }
  }, [selectedOrgUnit, actionPlan, actionPlanValue, setActions, resetTouched]);

  useEffect(() => {
    resetErrors();
  }, [resetErrors, actionPlanId]);

  useEffect(() => {
    checkForWritePermission();
  }, []);

  return (
    <PageFrame
      className="PlanActionDetailPage-root"
      sx={{
        ...styles.planActionDetailPage,
        ...(Array.isArray(sx) ? sx : [sx]),
      }}
    >
      <OrgUnitHeader />
      <Box
        sx={{
          mb: 5,
        }}
      >
        <PlanActionMeta
          actionPlanValue={actionPlanValue}
          editable={editable}
          onDelete={handleDelete}
          onNameChange={handleNameChange}
          onSave={handleSave}
          showFollowUpInspirations={
            !!selectedOrgUnit?.follow_up_inspirations_link
          }
        />
      </Box>
      {actions.map((actionId, index) => (
        <Action
          actionId={actionId}
          actionPlanId={actionPlan ? actionPlan.id : null}
          editable={editable}
          errors={errors && errors.actions ? errors.actions[index] : null}
          index={index}
          key={actionId}
        />
      ))}
      {actions.length < 10 && editable && <CreateActionButton />}
    </PageFrame>
  );
};

export default PlanActionDetailPage;
